import { useMemo } from "react";
import { Card, Stack, Typography } from "@altimateai/lego";
import DatabricksCostChartSkeleton from "@/pages/databricks/summary/components/DatabricksCostChartSkeleton";
import { useSingleJobRunTrends } from "@/pages/jobs/hooks";
import { useAutoTuneSavingsTrends } from "./hooks";
import type { AutoTuneSavingsStackedPoint, AutoTuneSavingsTrendsResponse } from "./types";
import AutoTuneSavingsChart from "./charts/AutoTuneSavingsChart";

interface Props {
  jobId: string;
  instanceId?: string | number;
  /** Calendar-filter bounds (YYYY-MM-DD) from the job-detail page. The BE
   *  scopes savings on the run window (``period_start_time``), so these
   *  rescope the chart when the user changes the date range. The card
   *  renders nothing until both bounds are available. */
  startDate?: string;
  endDate?: string;
}

interface CostByPeriod {
  [period: string]: number;
}

// Merge the savings ``actual``/``potential`` series with the per-day job
// run cost into one row per day, mirroring the Snowflake warehouse stack:
// spend + potential + realized savings. Each savings segment keeps its
// DBU/infra split for the tooltip. Sorted by period for a left-to-right
// time axis. Exported for direct unit testing.
export const mergeSeries = (
  savings: AutoTuneSavingsTrendsResponse | undefined,
  costByPeriod: CostByPeriod
): AutoTuneSavingsStackedPoint[] => {
  const byPeriod = new Map<string, AutoTuneSavingsStackedPoint>();
  const get = (period: string): AutoTuneSavingsStackedPoint => {
    let point = byPeriod.get(period);
    if (!point) {
      point = {
        period,
        realized: 0,
        potential: 0,
        spend: 0,
        realizedDbu: 0,
        realizedInfra: 0,
        potentialDbu: 0,
        potentialInfra: 0,
      };
      byPeriod.set(period, point);
    }
    return point;
  };

  for (const p of savings?.actual ?? []) {
    const point = get(p.period);
    point.realizedDbu = p.dbu_savings;
    point.realizedInfra = p.infra_savings;
    point.realized = p.dbu_savings + p.infra_savings;
  }
  for (const p of savings?.potential ?? []) {
    const point = get(p.period);
    point.potentialDbu = p.dbu_savings;
    point.potentialInfra = p.infra_savings;
    point.potential = p.dbu_savings + p.infra_savings;
  }
  for (const [period, cost] of Object.entries(costByPeriod)) {
    get(period).spend = cost;
  }

  return [...byPeriod.values()].sort((a, b) => a.period.localeCompare(b.period));
};

// Single stacked daily chart on the Auto Tune tab, mirroring the Snowflake
// warehouse auto-manage graph: actual run cost (spend) under potential and
// realized (auto-tune) savings. Savings come from ``dbx_jobs_cost_savings``;
// the per-day cost reuses the job run-trends endpoint.
const AutoTuneSavingsCard = ({ jobId, instanceId, startDate, endDate }: Props) => {
  const hasDateRange = Boolean(startDate && endDate);
  const savingsQuery = useAutoTuneSavingsTrends(
    jobId,
    instanceId,
    startDate ?? "",
    endDate ?? "",
    hasDateRange
  );
  const trendsQuery = useSingleJobRunTrends(
    jobId,
    { start_date: startDate, end_date: endDate, granularity: "day" },
    Boolean(jobId) && hasDateRange
  );

  const costByPeriod = useMemo<CostByPeriod>(() => {
    const map: CostByPeriod = {};
    for (const t of trendsQuery.data?.trends ?? []) {
      map[t.period] = t.estimated_cost_usd ?? 0;
    }
    return map;
  }, [trendsQuery.data]);

  const points = useMemo(
    () => mergeSeries(savingsQuery.data, costByPeriod),
    [savingsQuery.data, costByPeriod]
  );

  if (!hasDateRange) {
    return null;
  }

  const isLoading = savingsQuery.isLoading || trendsQuery.isLoading;

  return (
    <Card className="al-p-4">
      <Stack direction="column" gap="none" className="al-mb-4">
        <Typography size="lg" weight="medium">
          Cost Savings
        </Typography>
        <Typography size="xs" className="al-text-muted-foreground">
          Daily · spend, realized + potential savings
        </Typography>
      </Stack>
      {isLoading ? (
        <DatabricksCostChartSkeleton />
      ) : (
        <AutoTuneSavingsChart data={points} granularity="day" />
      )}
    </Card>
  );
};

export default AutoTuneSavingsCard;
